import type { ArrayValues } from '../helpers/types';

export const palette = {
  blue25: '#F2F6FF',
  blue50: '#E3EDFF',
  blue100: '#B7D1FF',
  blue200: '#82AFFF',
  blue300: '#4E8EFF',
  blue400: '#196CFF',
  blue500: '#1044CC',
  blue600: '#092699',
  blue700: '#041166',
  blue400Alpha: 'rgba(25, 108, 255, 0.12)',

  white: '#ffffff',
  black: '#000000',
  darkGrey: '#031B4A',

  neutral50: '#F9F9F9',
  neutral100: '#DEDEDE',
  neutral200: '#BABABA',
  neutral300: '#8D8D8D',
  neutral400: '#565656',
  neutral500: '#494848',
  neutral600: '#2F2F2F',
  neutral700: '#141414',

  grey50: '#FBFCFC',
  grey100: '#F7F9FA',
  grey200: '#E2E6EE',
  grey300: '#C9D1DF',
  grey400: '#93A3C0',
  grey500: '#56709C',
  grey600: '#2B497C',
  grey700: '#0F2D61',
  grey300alpha: '#c9d1df4d',

  green50: '#E2F7E8',
  green100: '#A0E6B3',
  green200: '#70DA8D',
  green300: '#41CE67',
  green400: '#12C242',
  green500: '#0B9029',
  green600: '#066517',
  green700: '#023E0A',
  green400alpha: 'rgba(18, 194, 66, 0.12)',

  yellow50: '#FEF6E4',
  yellow100: '#FDDD94',
  yellow200: '#FCD16F',
  yellow300: '#FBC549',
  yellow400: '#FBBA25',
  yellow500: '#E09F1D',
  yellow600: '#C88417',
  yellow700: '#B36611',
  yellow400alpha: 'rgba(251, 186, 37, 0.12)',

  orange50: '#FEEEE4',
  orange100: '#FDC06A',
  orange200: '#FDA753',
  orange300: '#FC8D3B',
  orange400: '#FB7325',
  orange500: '#E3581C',
  orange600: '#C74917',
  orange700: '#A63712',
  orange400alpha: 'rgba(251, 115, 37, 0.12)',

  red50: '#FFE5EA',
  red100: '#FFA8BA',
  red200: '#FF7D97',
  red300: '#FF5275',
  red400: '#FF2753',
  red500: '#E82548',
  red600: '#CE213D',
  red700: '#AD152E',
  red400alpha: 'rgba(255, 39, 83, 0.12)',

  purple50: '#F5E5FA',
  purple100: '#D28BE7',
  purple200: '#C66AE1',
  purple300: '#BA4BDB',
  purple400: '#AE2CD5',
  purple500: '#851DAD',
  purple600: '#621289',
  purple700: '#440966',
  purple400alpha: 'rgba(174, 44, 213, 0.12)',
} as const;
export type Palette = typeof palette;

export const paletteColors = ['blue', 'grey', 'green', 'yellow', 'orange', 'red', 'purple'] as const;
export type PaletteColor = ArrayValues<typeof paletteColors>;

export const colors = {
  primary: {
    light: palette.blue50,
    normal: palette.blue400,
    dark: palette.blue500,
    darker: palette.blue600,
    alpha: palette.blue400Alpha,
    textLight: palette.blue200,
    textRegular: palette.blue400,
    textDark: palette.blue700,
  },
  danger: {
    light: palette.red50,
    normal: palette.red400,
    dark: palette.red500,
    darker: palette.red600,
    alpha: palette.red400alpha,
    textLight: palette.red200,
    textRegular: palette.red400,
    textDark: palette.red700,
  },
  success: {
    light: palette.green50,
    normal: palette.green400,
    dark: palette.green500,
    darker: palette.green600,
    alpha: palette.green400alpha,
    textLight: palette.green200,
    textRegular: palette.green400,
    textDark: palette.green700,
  },
  warning: {
    light: palette.orange50,
    normal: palette.yellow400,
    dark: palette.orange500,
    darker: palette.orange600,
    alpha: palette.orange400alpha,
    textLight: palette.orange200,
    textRegular: palette.orange400,
    textDark: palette.orange700,
  },
  body: {
    text: palette.darkGrey,
    textLight: palette.white,
    textDark: palette.grey600,
    background: palette.white,
  },
  disabled: {
    text: palette.grey400,
    background: palette.grey200,
  },
  border: palette.grey200,
  hover: palette.grey100,
} as const;

export const textColors = {
  primary: colors.primary.textRegular,
  'primary-light': colors.primary.textLight,
  'primary-dark': colors.primary.textDark,
  danger: colors.danger.normal,
  'danger-light': colors.danger.textLight,
  'danger-dark': colors.danger.textDark,
  success: colors.success.normal,
  'success-light': colors.success.textLight,
  'success-dark': colors.success.textDark,
  warning: colors.warning.normal,
  'warning-light': colors.warning.textLight,
  'warning-dark': colors.warning.textDark,
  body: colors.body.text,
  'body-dark': colors.body.textDark,
  'body-light': colors.body.textLight,
  disabled: colors.disabled.text,
};

export type Colors = typeof colors;

export type ColorVariant = keyof Colors;

export type TextColor = keyof typeof textColors;
